import "./styles/main.scss";
import "@/api/scayla";

import { registerFloatingFilterDirective, SCA_RICH_TEXT_ENGINE } from "@scayla/components";
import { createOidc, handleError, oidcSettings, useAuth, useLogger, useOidcStore, useSentry } from "@scayla/core";
import type { VueQueryPluginOptions } from "@tanstack/vue-query";
import { VueQueryPlugin } from "@tanstack/vue-query";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import type { Component } from "vue";
import { createApp } from "vue";

import App from "./App.vue";
import { tooltip } from "./directives/tooltip.ts";
import { i18n } from "./i18n";
import { router } from "./router";
import { useUserSettingsStore } from "./store/userSettings";

const logger = useLogger("main");
const oidcStore = useOidcStore();
const auth = useAuth();

void createOidc({
  oidcSettings: oidcSettings,
  auth: true,
  refreshToken: {
    enable: true,
    time: 30000,
  },
  events: {
    addUserSignedIn: () => {
      logger.debug("addUserSignedIn")();
    },
    addUserLoaded: (user) => {
      logger.debug("addUserLoaded", user)();
      oidcStore.actions.value.setUser(user);
    },
    addUserSignedOut: () => {
      // Only clear local state here — do not force a redirect while this tab may be
      // backgrounded. syncUserFromStorage() reconciles once the tab is actually visible.
      logger.debug("addUserSignedOut")();
      oidcStore.actions.value.removeUser();
    },
    addAccessTokenExpired: (smth) => {
      logger.debug("addAccessTokenExpired", smth)();
    },
    addAccessTokenExpiring: (smth) => {
      logger.debug("addAccessTokenExpiring", smth)();
    },
    addSilentRenewError: (err) => {
      logger.debug("addSilentRenewError", err)();
    },
  },
});

/**
 * Reconciles this tab's in-memory oidc user against what's currently persisted in storage.
 * Only called while the tab is visible/foregrounded, so any redirect/reload it triggers
 * happens in a safe, non-throttled context instead of from a backgrounded tab.
 */
async function syncUserFromStorage() {
  const userManager = oidcStore.state.value.userManager;
  if (!userManager) {
    return;
  }

  const storedUser = await userManager.getUser();
  const currentSub = oidcStore.state.value.user?.profile.sub;

  if (!storedUser) {
    if (currentSub) {
      logger.debug("Session ended in another tab — redirecting to login")();
      oidcStore.actions.value.removeUser();
      void auth.signinRedirect();
    }
    return;
  }

  if (storedUser.profile.sub !== currentSub) {
    // A different user is now the active session — reload so tenant/permissions/query
    // caches get re-derived from scratch instead of mixing state from two identities.
    logger.debug("Different user session detected — reloading")();
    window.location.reload();
    return;
  }

  oidcStore.actions.value.setUser(storedUser);
}

window.addEventListener("storage", (event) => {
  // event.key is null when another tab cleared localStorage entirely (e.g. silent-renew.ts on login_required)
  const isOidcKey = event.key === null || event.key?.startsWith("oidc.user:");
  if (isOidcKey && document.visibilityState === "visible") {
    void syncUserFromStorage();
  }
});

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    void syncUserFromStorage();
  }
});

const app = createApp(App as Component);

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

app.use(pinia);
app.use(i18n);
app.use(router);
app.directive("tooltip", tooltip);
registerFloatingFilterDirective("tooltip", tooltip);

const vueQueryPluginOptions: VueQueryPluginOptions = {
  enableDevtoolsV6Plugin: true,
  queryClientConfig: {
    defaultOptions: {
      queries: {
        staleTime: 200,
        experimental_prefetchInRender: false,
        structuralSharing: false,
        refetchOnWindowFocus: false,
      },
    },
  },
};
app.use(VueQueryPlugin, vueQueryPluginOptions);

useSentry(app, router);

// Every rich text editor in the app, inside AppView or not, opens the user's engine. The store is made on first use, in
// the app's context, as its queries need the plugins above.
let userSettingsStore: ReturnType<typeof useUserSettingsStore> | undefined;
app.provide(SCA_RICH_TEXT_ENGINE, () => {
  userSettingsStore ??= app.runWithContext(() => useUserSettingsStore());
  return userSettingsStore.richTextEngine;
});

// Uncomment to enable Vue performance marks in DevTools (dev only)
// app.config.performance = true;

// Default Error Handler
app.config.errorHandler = (error) => handleError(error);
// Handle Errors outside Vue
window.addEventListener("error", (error) => handleError(error), { passive: true });
// Handle errors in Promises that were not caught (for example async watchers)
window.addEventListener("unhandledrejection", (error) => handleError(error), { passive: true });

app.mount("#app");
